import React, { useRef, useState, useEffect, useCallback } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  UncontrolledTooltip,
} from "reactstrap";

import SearchableDropdown from "components/DropDown/SearchableDropdown";
import ReactBsTable from "components/Tables/ReactBsTable";

import { uploadReportingRawCsv, getCsvLogList } from "actions/users";
import { DATE_RANGE_OPTIONS, CSV_STATUS_OPTIONS } from "variables/constants";
import { reportingProcess } from "actions/reporting";
import NotificationAlert from "react-notification-alert";
import { getDemandPartnerList } from "actions/reporting";
import DynamicFormModal from "components/Modal/Form";

const sortFieldMap = {
  userName: "Name",
  uploadedBy: "UploadedBy",
  status: "Status",
  startDate: "StartDate",
  endDate: "EndDate",
  createdOn: "CreatedAt",
};

const Data = () => {
  const fileInputRef = useRef(null);
  const notificationAlertRef = useRef(null);

  const [csvData, setCSVData] = useState({
    data: [],
    totalRecords: 0,
  });
  const [pageNum, setPageNum] = useState(1);
  const pageSize = 10;

  const [filters, setFilters] = useState({
    name: "",
    date_range: "",
    created_start_date: "",
    created_end_date: "",
    status: "",
  });

  const [searchParams, setSearchParams] = useState({
    pageNum: 1,
    filterParams: [],
    searchText: "",
  });

  const [showModal, setShowModal] = useState({
    show: false,
    formType: "Upload CSV",
  });
  const [formData, setFormData] = useState({
    demandPartner: "",
    csvFile: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [showLoader, setShowLoader] = useState(false);
  const [demandPartnerOptions, setDemandPartnerOptions] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    dataField: "",
    sortField: "",
    sortOrder: "",
  });

  // ✅ FIXED: Initialize formState properly
  const [formState, setFormState] = useState({
    values: {
      csvFile: null,
    },
    errors: {},
  });

  // Define form fields for DynamicFormModal
  const getUploadFormFields = () => {
    return [
      {
        name: "demandPartner",
        label: "Demand Partner",
        type: "dropdown",
        required: true,
        isSearchable: true,
        options: demandPartnerOptions,
        isMulti: false,
        hasMore: true,
        placeholder: "Select Demand Partner",
      },
    ];
  };

  React.useEffect(() => {
    const fetchDemandPartners = async () => {
      try {
        const res = await getDemandPartnerList();
        const demandPartnerData =
          res?.code === 200
            ? (res?.data?.demandPartner || []).map((item) => ({
                label: item?.name || "",
                value: item?.ID,
              }))
            : [];
        setDemandPartnerOptions(demandPartnerData);
      } catch (error) {
        console.error("Error fetching demand partners:", error);
      }
    };

    fetchDemandPartners();
  }, []);

  // ✅ FIXED FILTER BUILDER - NOW USING CREATED AT
  const buildFilterParams = useCallback(() => {
    const filterParams = [];

    if (filters.status) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters.status],
      });
    }

    // Date range filter for CreatedAt - using "btw" condition
    if (filters.created_start_date && filters.created_end_date) {
      filterParams.push({
        field: "CreatedAt",
        condition: "btw",
        filterValues: [
          moment(filters.created_start_date).format("YYYY-MM-DD"),
          moment(filters.created_end_date).format("YYYY-MM-DD"),
        ],
      });
    } else if (filters.created_start_date) {
      filterParams.push({
        field: "CreatedAt",
        condition: "gteq",
        filterValues: [moment(filters.created_start_date).format("YYYY-MM-DD")],
      });
    } else if (filters.created_end_date) {
      filterParams.push({
        field: "CreatedAt",
        condition: "lteq",
        filterValues: [moment(filters.created_end_date).format("YYYY-MM-DD")],
      });
    }

    if (filters.name && filters.name.trim() !== "") {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filters.name.trim()],
      });
    }

    return filterParams;
  }, [
    filters.status,
    filters.created_start_date,
    filters.created_end_date,
    filters.name,
  ]);

  // ✅ COMBINED FETCH DATA - removed separate sort useEffect
  useEffect(() => {
    const fetchCsvLogs = async () => {
      try {
        const response = await getCsvLogList(
          searchParams.pageNum,
          searchParams.filterParams,
          searchParams.searchText,
          sortConfig.sortField,
          sortConfig.sortOrder,
        );

        setCSVData({
          data: response?.data?.CsvLog || [],
          totalRecords: response?.data?.totalRecords || 0,
        });
      } catch (error) {
        console.error("Error fetching CSV logs:", error);
      }
    };

    fetchCsvLogs();
  }, [searchParams.pageNum, searchParams.filterParams, searchParams.searchText, sortConfig.sortField, sortConfig.sortOrder]);

  // ✅ REMOVED the separate sort useEffect - now combined with above

  // ✅ SEARCH
  const handleSearch = () => {
    const filterParams = buildFilterParams();

    setSearchParams({
      pageNum: 1,
      filterParams,
      searchText: filters.name,
    });

    setPageNum(1);
  };

  // ✅ PAGE CHANGE
  const handlePageChange = (page) => {
    setPageNum(page);
    setSearchParams((prev) => ({
      ...prev,
      pageNum: page,
    }));
  };

  // ✅ FILTER CHANGE - UPDATED FOR CREATED DATES
  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      if (key === "created_start_date") {
        const startDate = value;
        const endDate = prev.created_end_date;

        if (endDate && new Date(startDate) <= new Date(endDate)) {
          return {
            ...prev,
            created_start_date: startDate,
          };
        }

        return {
          ...prev,
          created_start_date: startDate,
          created_end_date: "",
        };
      }

      return {
        ...prev,
        [key]: value,
      };
    });
  };

  // ✅ DATE RANGE CHANGE - UPDATED FOR CREATED DATES
  const handleDateRangeChange = (value) => {
    const today = moment().format("YYYY-MM-DD");

    if (value === "Custom Range") {
      setFilters((prev) => ({
        ...prev,
        date_range: value,
        created_start_date: "",
        created_end_date: "",
      }));
      return;
    }

    let months = 0;
    if (value === "Last 1 Month") months = 1;
    if (value === "Last 3 Month") months = 3;
    if (value === "Last 6 Month") months = 6;
    if (value === "Last 12 Month") months = 12;

    const startDate = moment().subtract(months, "months").format("YYYY-MM-DD");

    setFilters((prev) => ({
      ...prev,
      date_range: value,
      created_start_date: startDate,
      created_end_date: today,
    }));
  };

  // ✅ FORMAT DATE
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // ✅ CHECK IF ANY FILTER IS APPLIED
  const isAnyFilterApplied = (filters) => {
    return Object.entries(filters).some(([key, value]) => {
      return value !== "" && value !== null && value !== undefined;
    });
  };

  // ✅ RESET
  const handleReset = () => {
    setFilters({
      name: "",
      date_range: "",
      created_start_date: "",
      created_end_date: "",
      status: "",
    });

    setSearchParams({
      pageNum: 1,
      filterParams: [],
      searchText: "",
    });

    setPageNum(1);
  };

  const handleManageModal = () => {
    setShowModal({
      show: false,
      formType: "Upload CSV",
    });
    setFormData({
      demandPartner: "",
      csvFile: null,
    });
    setFormErrors({});
    // ✅ Reset formState properly
    setFormState({
      values: {
        csvFile: null,
      },
      errors: {},
    });
  };

  // ✅ FIXED: Handle input change for DynamicFormModal
  // In your Data.js component, update the handleInputChange function to handle the new signature:
  const handleInputChange = (name, value, fieldType) => {
    // Clear error for the field being changed
    if (formErrors[name]) {
      const newErrors = { ...formErrors };
      delete newErrors[name];
      setFormErrors(newErrors);
    }

    if (fieldType === "file") {
      // Handle file input
      const file = value;

      // Check if file is selected
      if (!file) {
        setFormData({
          ...formData,
          [name]: null,
        });
        setFormState({
          ...formState,
          values: {
            ...formState.values,
            [name]: null,
          },
        });
        return;
      }

      // Validate file extension for CSV
      if (
        name === "csvFile" &&
        file.name &&
        !file.name.toLowerCase().endsWith(".csv")
      ) {
        setFormErrors({
          ...formErrors,
          [name]: "Only CSV files are allowed",
        });
        setFormState({
          ...formState,
          values: {
            ...formState.values,
            [name]: file,
          },
          errors: {
            ...formState.errors,
            [name]: "Only CSV files are allowed",
          },
        });
        setFormData({
          ...formData,
          [name]: file,
        });
        return;
      }

      // Validate file size
      const MAX_FILE_SIZE = 10 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        setFormErrors({
          ...formErrors,
          [name]: "File size should not exceed 10 MB",
        });
        setFormState({
          ...formState,
          values: {
            ...formState.values,
            [name]: file,
          },
          errors: {
            ...formState.errors,
            [name]: "File size should not exceed 10 MB",
          },
        });
        setFormData({
          ...formData,
          [name]: file,
        });
        return;
      }

      // Clear any existing file error
      if (formErrors[name]) {
        const newErrors = { ...formErrors };
        delete newErrors[name];
        setFormErrors(newErrors);
      }

      setFormData({
        ...formData,
        [name]: file,
      });
      setFormState({
        ...formState,
        values: {
          ...formState.values,
          [name]: file,
        },
        errors: {
          ...formState.errors,
          [name]: null,
        },
      });
    } else {
      // Handle regular input/dropdown change
      setFormData({
        ...formData,
        demandPartner: name.target.value,
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.demandPartner) {
      errors.demandPartner = "Demand Partner is required";
    }

    if (!formState.values.csvFile && !formData.csvFile) {
      errors.csvFile = "CSV file is required";
    } else {
      const file = formState.values.csvFile || formData.csvFile;
      if (file && file.name && !file.name.toLowerCase().endsWith(".csv")) {
        errors.csvFile = "Only CSV files are allowed";
      }

      if (file && file.size) {
        const MAX_FILE_SIZE = 10 * 1024 * 1024;
        if (file.size > MAX_FILE_SIZE) {
          errors.csvFile = "File size should not exceed 10 MB";
        }
      }
    }

    setFormErrors(errors);
    setFormState({
      ...formState,
      errors: errors,
    });
    return Object.keys(errors).length === 0;
  };

  // ✅ FIXED: Handle form submission
  const handleSubmit = async (e) => {
    // Prevent default form submission and page refresh
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!validateForm()) {
      return;
    }

    setShowLoader(true);

    try {
      const formDataToSend = new FormData();
      const fileToUpload = formState.values.csvFile || formData.csvFile;
      formDataToSend.append("dailyGAMCsv", fileToUpload);
      formDataToSend.append("demandPartner", formData.demandPartner);

      const res = await uploadReportingRawCsv(formDataToSend);

      if (res.code === 200) {
        notify("success", "File uploaded successfully");
        handleManageModal(); // Close modal
        handleSearch(); // Refresh the table data
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      console.error("Upload failed", error);
      notify("danger", "File upload failed");
    } finally {
      setShowLoader(false);
    }
  };

  // ✅ FILE UPLOAD (for direct upload button)
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name?.toLowerCase().endsWith(".csv")) {
      if (notificationAlertRef.current) {
        notificationAlertRef.current.notificationAlert({
          place: "tr",
          message: <div>Only CSV files are allowed</div>,
          type: "danger",
          icon: "tim-icons icon-bell-55",
          autoDismiss: 5,
        });
      }
      e.target.value = "";
      return;
    }

    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      if (notificationAlertRef.current) {
        notificationAlertRef.current.notificationAlert({
          place: "tr",
          message: <div>File size should not exceed 10 MB</div>,
          type: "danger",
          icon: "tim-icons icon-bell-55",
          autoDismiss: 5,
        });
      }
      e.target.value = "";
      return;
    }

    try {
      const formData = new FormData();
      formData.append("dailyGAMCsv", file);

      const res = await uploadReportingRawCsv(formData);

      if (res.code === 200) {
        notificationAlertRef.current.notificationAlert({
          place: "tr",
          message: <div>File uploaded successfully</div>,
          type: "success",
          icon: "tim-icons icon-bell-55",
          autoDismiss: 5,
        });
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notificationAlertRef.current.notificationAlert({
          place: "tr",
          message: <div>{message}</div>,
          type: "danger",
          icon: "tim-icons icon-bell-55",
          autoDismiss: 5,
        });
      }

      handleSearch();
    } catch (error) {
      console.error("Upload failed");
      if (notificationAlertRef.current) {
        notificationAlertRef.current.notificationAlert({
          place: "tr",
          message: <div>File upload failed</div>,
          type: "danger",
          icon: "tim-icons icon-bell-55",
          autoDismiss: 5,
        });
      }
    }

    e.target.value = null;
  };

  //===> notify function is used for to show notification on create and update the deal.
  const notify = (type, message) => {
    if (notificationAlertRef.current) {
      notificationAlertRef.current.notificationAlert({
        place: "tr",
        message: <div>{message}</div>,
        type,
        icon: "tim-icons icon-bell-55",
        autoDismiss: 5,
      });
    }
  };

  const handleUploadCsvFile = () => {
    setShowModal({
      show: true,
      formType: "Upload CSV",
    });
    setFormData({
      demandPartner: "",
      csvFile: null,
    });
    setFormErrors({});
    setFormState({
      values: {
        csvFile: null,
      },
      errors: {},
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "PROCESSED":
        return "success";
      case "COMPLETED":
        return "info";
      case "FAILED":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case "PROCESSING":
        return "IN PROGRESS";
      case "COMPLETED":
        return "PROCESSED";
      default:
        return status;
    }
  };

  const handleProcessData = async (row) => {
    try {
      const res = await reportingProcess(row?.ID);
      if (res.code === 200) {
        notify("success", res?.message);
        const response = await getCsvLogList(
          searchParams.pageNum,
          searchParams.filterParams,
          searchParams.searchText,
          sortConfig.sortField,
          sortConfig.sortOrder,
        );

        setCSVData({
          data: response?.data?.CsvLog || [],
          totalRecords: response?.data?.totalRecords || 0,
        });
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      console.error("Process data error:", error);
      notify("danger", "Failed to process data");
    }
  };

  //Sort Function
  const handleSortChange = (dataField, sortOrder) => {
    const mappedSortField = sortFieldMap[dataField] || dataField;
    setSortConfig({ dataField, sortField: mappedSortField, sortOrder });
    // setPageNum(1);
  };

  // ✅ TABLE COLUMNS
  const columns = [
    // {
    //   text: "Sr. No.",
    //   dataField: "srNo",
    //   formatter: (_, __, rowIndex) => (pageNum - 1) * pageSize + rowIndex + 1,
    // },
    {
      dataField: "userName",
      text: "Uploaded By",
      formatter: (_, row) => {
        if (row?.userName) {
          return `${row.userName}`.trim() || "-";
        }
        return "-";
      },
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "csvPath",
      text: "File Name",
      formatter: (_, row) => (row?.csvPath ? <span>{row.csvPath}</span> : "-"),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell) => (
        <Badge color={getStatusColor(cell)}>{getStatusDisplay(cell)}</Badge>
      ),
      // sort: true,
      // sortCaret: (order) => {
      //   if (!order) {
      //     return (
      //       <span className="sort-caret" style={{ marginLeft: "8px" }}>
      //         <i className="fa fa-sort" />
      //       </span>
      //     );
      //   }
      //   return order === "asc" ? (
      //     <span className="sort-caret" style={{ marginLeft: "8px" }}>
      //       <i className="fa fa-sort-up" />
      //     </span>
      //   ) : (
      //     <span className="sort-caret" style={{ marginLeft: "8px" }}>
      //       <i className="fa fa-sort-down" />
      //     </span>
      //   );
      // },
    },
    {
      dataField: "startDate",
      text: "Data Start",
      formatter: (_, row) => {
        if (row?.startDate) {
          return moment(row.startDate).format("DD MMM YYYY");
        }
        return "-";
      },
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "endDate",
      text: "Data End",
      formatter: (_, row) => {
        if (row?.endDate) {
          return moment(row.endDate).format("DD MMM YYYY");
        }
        return "-";
      },
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "source",
      text: "Source",
      formatter: (_, row) => {
        if (row?.source) {
          return row.source === "IMAP" ? "Email Sync" : "Uploaded";
        }
        return "-";
      },
    },
    {
      dataField: "createdOn",
      text: "Created On",
      formatter: (_, row) => {
        if (row?.createdOn) {
          return moment(row.createdOn).format("DD MMM YYYY HH:mm");
        }
        return "-";
      },
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      text: "Actions",
      dataField: "actions",
      formatter: (_, row) => {
        const rowId = row?.ID;
        return (
          <>
            <a
              id={`reprocessed${rowId}`}
              className="table-action table-action-success"
              onClick={(e) => {
                e.preventDefault();
                handleProcessData(row);
              }}
              href="#"
            >
              <i className="fas fa-refresh" />
            </a>
            <UncontrolledTooltip target={`reprocessed${rowId}`}>
              Reprocess Data
            </UncontrolledTooltip>

            {row?.status === "COMPLETED" && (
              <>
                <a
                  id={`download${rowId}`}
                  className="table-action table-action-success ml-2"
                  onClick={(e) => e.preventDefault()}
                  href="#"
                >
                  <i className="fas fa-download" />
                </a>
                <UncontrolledTooltip target={`download${rowId}`}>
                  Download File
                </UncontrolledTooltip>
              </>
            )}
          </>
        );
      },
    },
  ];

  // ✅ FILTER SECTION COMPONENT - UPDATED FOR CREATED DATES
  const filterSection = () => {
    return (
      <>
        <Row className="align-items-center">
          <Col md={2}>
            <input
              className="form-control"
              placeholder="Search by name"
              value={filters.name}
              onChange={(e) => handleFilterChange("name", e.target.value)}
            />
          </Col>
          <Col md={2}>
            <SearchableDropdown
              options={CSV_STATUS_OPTIONS}
              placeholder="Select Status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              isSearchable={false}
            />
          </Col>
        </Row>

        <Row className="mt-2">
          <Col md={2}>
            <SearchableDropdown
              options={DATE_RANGE_OPTIONS}
              placeholder="Created Date Range"
              value={filters.date_range}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              isSearchable={false}
            />
          </Col>
          <Col md={2}>
            <div className="datepicker-wrapper">
              <DatePicker
                selected={
                  filters.created_start_date
                    ? new Date(filters.created_start_date)
                    : null
                }
                onChange={(date) =>
                  handleFilterChange(
                    "created_start_date",
                    date ? formatDate(date) : "",
                  )
                }
                dateFormat="dd MMM yyyy"
                placeholderText="Created Start Date"
                className="form-control custom-datepicker"
                disabled={filters.date_range !== "Custom Range"}
              />
              <i className="ni ni-calendar-grid-58 text-muted calendar-icon" />
            </div>
          </Col>

          <Col md={2}>
            <div className="datepicker-wrapper">
              <DatePicker
                selected={
                  filters.created_end_date
                    ? new Date(filters.created_end_date)
                    : null
                }
                onChange={(date) =>
                  handleFilterChange(
                    "created_end_date",
                    date ? formatDate(date) : "",
                  )
                }
                dateFormat="dd MMM yyyy"
                placeholderText="Created End Date"
                className="form-control custom-datepicker"
                disabled={
                  filters.date_range !== "Custom Range" ||
                  !filters.created_start_date
                }
                minDate={
                  filters.created_start_date
                    ? new Date(filters.created_start_date)
                    : undefined
                }
              />
              <i className="ni ni-calendar-grid-58 text-muted calendar-icon" />
            </div>
          </Col>

          <Col md={4}>
            <Button
              className="filter-btn-gradient-primary btn-md responsive"
              onClick={handleSearch}
              type="button"
            >
              Search
            </Button>

            <Button
              className="secondary"
              disabled={!isAnyFilterApplied(filters)}
              onClick={handleReset}
              type="button"
            >
              Reset
            </Button>
          </Col>
        </Row>

        {searchParams.searchText && (
          <Row className="mt-1">
            <Col>
              <small className="text-muted">
                Search Text: "{searchParams.searchText}"
              </small>
            </Col>
          </Row>
        )}
      </>
    );
  };

  // ✅ MAIN RENDER
  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container className="mt--6" fluid>
        <Card>
          <ReactBsTable
            title="CSV File Data"
            description="Manage CSV Uploads"
            data={csvData?.data}
            columns={columns}
            keyField="ID"
            addbtntext="Upload CSV File"
            onAddBtnClick={handleUploadCsvFile}
            renderFilters={filterSection}
            page={pageNum}
            sizePerPage={pageSize}
            totalRecords={csvData?.totalRecords}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            defaultSortField={sortConfig.dataField}
            defaultSortOrder={sortConfig.sortOrder}
            showLoader={showLoader}
          />
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </Card>
      </Container>
      {/* Dynamic Form Modal for CSV Upload */}
      <DynamicFormModal
        isOpen={showModal.show}
        toggle={handleManageModal}
        title={showModal.formType}
        fields={getUploadFormFields()}
        formData={formData}
        showDocumentUpload={true}
        formState={formState}
        isCsv={true}
        setFormState={setFormState}
        errors={formErrors}
        onChange={handleInputChange}
        onSubmit={handleSubmit}
        showLoader={showLoader}
      />
    </>
  );
};

export default Data;